import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Plus, 
  History, 
  TrendingUp, 
  ChevronRight,
  Droplets,
  Calendar,
  User,
  LogOut,
  Sparkles,
  Sun,
  Leaf,
  CheckCircle,
  ArrowRight,
  HelpCircle,
  Bell,
  Baby,
  X
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { CureReminders } from '../components/cure';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = ({ onBack, onNewDiagnosis, onViewDiagnosis, onViewHelp, onBabyDiagnosis }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDiagnosisTypeSelector, setShowDiagnosisTypeSelector] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const diagnosesRes = await axios.get(`${API_URL}/diagnosis/history`);
      setDiagnoses(diagnosesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMM yyyy', {
      locale: language === 'fr' ? fr : enUS
    });
  };

  const latestDiagnosis = diagnoses[0];
  const skinScoreHistory = diagnoses.slice(0, 5).map(d => d.skin_score);
  const trend = skinScoreHistory.length >= 2 
    ? skinScoreHistory[0] - skinScoreHistory[skinScoreHistory.length - 1]
    : 0;

  const getConcernLabel = (concern) => {
    const labels = {
      acne: t('acne'),
      dark_spots: t('dark_spots'),
      wrinkles: t('wrinkles'),
      dehydration: t('dehydration'),
      sensitivity: t('sensitivity'),
      none: t('none')
    };
    return labels[concern] || concern;
  };

  return (
    <div className="min-h-screen bg-[#FDF8F5] pb-28 sm:pb-32 relative overflow-x-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-[#C9A086]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-40 right-10 w-40 h-40 bg-[#A67B5B]/10 rounded-full blur-3xl" />
      
      {/* Header */}
      <header className="relative z-20 px-4 py-4 border-b border-[#C9A086]/20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 rounded-full bg-[#C9A086]/10 text-[#5D4037] hover:bg-[#C9A086]/20 transition-colors"
              data-testid="dashboard-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A086] to-[#A67B5B] flex items-center justify-center shadow-md">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-[#5D4037]">SADASkinCare</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="px-3 py-1.5 rounded-full bg-[#C9A086]/10 text-sm font-medium text-[#5D4037] hover:bg-[#C9A086]/20 transition-colors"
              data-testid="dashboard-language-toggle"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            {onViewHelp && (
              <button
                onClick={onViewHelp}
                className="p-2 rounded-full bg-[#C9A086]/10 text-[#5D4037] hover:bg-[#C9A086]/20 transition-colors"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={logout}
              className="p-2 rounded-full bg-[#C9A086]/10 text-[#5D4037] hover:bg-[#C9A086]/20 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Content */}
            <div>
              {/* User Welcome */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C9A086] to-[#A67B5B] flex items-center justify-center shadow-lg">
                  <User className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-[#5D4037]/60 text-sm">
                    {language === 'fr' ? 'Bonjour,' : 'Hello,'}
                  </p>
                  <h2 className="text-xl font-semibold text-[#5D4037]">{user?.name || 'User'}</h2>
                </div>
              </div>

              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A086]/10 text-[#A67B5B] text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                <span>{language === 'fr' ? 'Spécialisé peau noire' : 'Specialized for black skin'}</span>
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4">
                <span className="text-[#C9A086] font-bold">SADA</span>
                <span className="text-[#5D4037] font-light">SkinCare</span>
              </h1>
              
              <h3 className="text-lg sm:text-xl text-[#5D4037] font-light italic mb-4">
                {language === 'fr' ? 'Diagnostic Dermo-Cosmétique' : 'Dermo-Cosmetic Diagnosis'}
              </h3>

              <p className="text-[#5D4037]/70 mb-6 leading-relaxed">
                {language === 'fr' 
                  ? 'Découvrez votre routine de peau personnalisée. Analyse complète avec routines matin, soir et compléments alimentaires.'
                  : 'Discover your personalized skin routine. Complete analysis with morning, evening routines and dietary supplements.'}
              </p>

              {/* CTA Button */}
              <Button
                onClick={() => setShowDiagnosisTypeSelector(true)}
                className="bg-[#C9A086] hover:bg-[#A67B5B] text-white text-lg px-8 py-6 h-auto rounded-full shadow-lg group transition-all duration-300 hover:shadow-xl"
                data-testid="new-diagnosis-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('newDiagnosis')}
                <div className="ml-3 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Button>

              {/* Diagnosis Type Selector Modal */}
              {showDiagnosisTypeSelector && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDiagnosisTypeSelector(false)}>
                  <Card className="w-full max-w-md bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-[#5D4037]">
                          {language === 'fr' ? 'Type de conseil' : 'Advice Type'}
                        </h3>
                        <button 
                          onClick={() => setShowDiagnosisTypeSelector(false)}
                          className="p-2 rounded-full hover:bg-gray-100"
                        >
                          <X className="w-5 h-5 text-gray-500" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        {/* Adult Diagnosis */}
                        <Card 
                          className="cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-[#C9A086]"
                          onClick={() => {
                            setShowDiagnosisTypeSelector(false);
                            onNewDiagnosis();
                          }}
                          data-testid="select-adult-diagnosis"
                        >
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C9A086] to-[#A67B5B] flex items-center justify-center shadow-lg">
                              <Sparkles className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-[#5D4037]">
                                {language === 'fr' ? 'Diagnostic Complet' : 'Complete Diagnosis'}
                              </h4>
                              <p className="text-sm text-[#5D4037]/60">
                                {language === 'fr' ? 'Peau, cheveux, ongles (adultes)' : 'Skin, hair, nails (adults)'}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#C9A086]" />
                          </CardContent>
                        </Card>
                        
                        {/* Baby Diagnosis */}
                        <Card 
                          className="cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-pink-400"
                          onClick={() => {
                            setShowDiagnosisTypeSelector(false);
                            if (onBabyDiagnosis) {
                              onBabyDiagnosis();
                            }
                          }}
                          data-testid="select-baby-diagnosis"
                        >
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center shadow-lg">
                              <Baby className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-[#5D4037]">
                                {language === 'fr' ? 'Conseil Bébé' : 'Baby Advice'}
                              </h4>
                              <p className="text-sm text-[#5D4037]/60">
                                {language === 'fr' ? 'Soins dermo-cosmétiques (0-36 mois)' : 'Dermo-cosmetic care (0-36 months)'}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-pink-500" />
                          </CardContent>
                        </Card>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 mt-8 pt-6 border-t border-[#C9A086]/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C9A086]/10 flex items-center justify-center flex-shrink-0">
                    <Sun className="w-5 h-5 text-[#C9A086]" />
                  </div>
                  <span className="text-sm text-[#5D4037]/70">
                    {language === 'fr' ? 'Analyse de peau' : 'Skin Analysis'}
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C9A086]/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-[#C9A086]" />
                  </div>
                  <span className="text-sm text-[#5D4037]/70">
                    {language === 'fr' ? 'Routine personnalisée' : 'Custom Routine'}
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C9A086]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C9A086]" />
                  </div>
                  <span className="text-sm text-[#5D4037]/70">
                    {language === 'fr' ? 'Compléments' : 'Supplements'}
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C9A086]/10 flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-5 h-5 text-[#C9A086]" />
                  </div>
                  <span className="text-sm text-[#5D4037]/70">
                    {language === 'fr' ? 'Actifs naturels' : 'Natural Actives'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="relative hidden lg:block">
              <div className="relative h-[500px] overflow-hidden rounded-3xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/r6v5n9dz_image.png"
                  alt="Beautiful African woman with healthy glowing skin"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FDF8F5]/50 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="relative z-10 px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Skin Score */}
            <Card className="border-[#C9A086]/20 bg-white/80 backdrop-blur-sm shadow-lg" data-testid="skin-score-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#C9A086]/10 flex items-center justify-center">
                    <Droplets className="w-5 h-5 text-[#C9A086]" />
                  </div>
                  {trend !== 0 && (
                    <Badge className={trend > 0 ? 'bg-green-100 text-green-700 border-0' : 'bg-red-100 text-red-700 border-0'}>
                      {trend > 0 ? '+' : ''}{trend}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-[#5D4037]/60 mb-1">{t('skinScore')}</p>
                <p className="text-3xl font-bold text-[#5D4037]">
                  {latestDiagnosis?.skin_score || '--'}
                </p>
              </CardContent>
            </Card>

            {/* Evolution */}
            <Card className="border-[#C9A086]/20 bg-white/80 backdrop-blur-sm shadow-lg" data-testid="evolution-card">
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-xl bg-[#A67B5B]/10 flex items-center justify-center mb-3">
                  <TrendingUp className="w-5 h-5 text-[#A67B5B]" />
                </div>
                <p className="text-sm text-[#5D4037]/60 mb-1">{t('skinEvolution')}</p>
                <p className="text-2xl font-bold text-[#5D4037]">
                  {diagnoses.length} <span className="text-base font-normal">{language === 'fr' ? 'analyses' : 'analyses'}</span>
                </p>
              </CardContent>
            </Card>

            {/* Last Diagnosis */}
            <Card className="border-[#C9A086]/20 bg-white/80 backdrop-blur-sm shadow-lg col-span-2" data-testid="last-diagnosis-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#C9A086]/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-[#C9A086]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5D4037]/60">
                        {language === 'fr' ? 'Dernier diagnostic' : 'Last diagnosis'}
                      </p>
                      <p className="font-medium text-[#5D4037]">
                        {latestDiagnosis 
                          ? formatDate(latestDiagnosis.created_at)
                          : (language === 'fr' ? 'Aucun' : 'None')
                        }
                      </p>
                    </div>
                  </div>
                  {latestDiagnosis && (
                    <Badge className="bg-[#C9A086]/10 text-[#A67B5B] border-0">
                      {getConcernLabel(latestDiagnosis.main_concern)}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Cure Reminders Section */}
      <section className="relative z-10 px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-[#5D4037]">
              {language === 'fr' ? 'Suivi des cures' : 'Cure Follow-up'}
            </h2>
          </div>
          <CureReminders language={language} />
        </div>
      </section>

      {/* Diagnosis History */}
      <section className="relative z-10 px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[#A67B5B]" />
            <h2 className="text-lg font-semibold text-[#5D4037]">{t('myDiagnoses')}</h2>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Card key={i} className="border-[#C9A086]/20 animate-pulse bg-white/50">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#C9A086]/10" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-[#C9A086]/10 rounded w-1/3" />
                      <div className="h-3 bg-[#C9A086]/10 rounded w-1/2" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : diagnoses.length === 0 ? (
            <Card className="border-[#C9A086]/20 border-dashed bg-white/50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#C9A086]/10 flex items-center justify-center mx-auto mb-4">
                  <History className="w-8 h-8 text-[#C9A086]/30" />
                </div>
                <p className="text-[#5D4037]/70 mb-2">{t('noDiagnoses')}</p>
                <p className="text-sm text-[#5D4037]/50">{t('startFirst')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {diagnoses.slice(0, 5).map((diagnosis, idx) => (
                <Card 
                  key={diagnosis.id} 
                  className="border-[#C9A086]/20 cursor-pointer hover:shadow-lg transition-all bg-white/80 backdrop-blur-sm hover:border-[#C9A086]/40"
                  onClick={() => onViewDiagnosis(diagnosis)}
                  data-testid={`diagnosis-history-${idx}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C9A086] to-[#A67B5B] flex items-center justify-center shadow-md">
                      <span className="font-bold text-white">{diagnosis.skin_score}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[#5D4037]">
                        {getConcernLabel(diagnosis.main_concern)}
                      </p>
                      <p className="text-sm text-[#5D4037]/60">
                        {formatDate(diagnosis.created_at)} • {diagnosis.analysis_method === 'ai_scan' 
                          ? (language === 'fr' ? 'Scan IA' : 'AI Scan')
                          : (language === 'fr' ? 'Questionnaire' : 'Questionnaire')
                        }
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#C9A086]" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
